import {
    Avatar,
    Box,
    Button,
    ButtonProps,
    HStack,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Portal,
    Spinner,
    useDisclosure
} from '@chakra-ui/react';
import { FaChevronDown, FaUser } from 'react-icons/fa';
import { FormattedMessage } from 'react-intl';

import { EventType } from '~/components/Event';
import { useFirebaseApp } from '~/lib/firebase';
import { redirectToLogin } from '~/lib/oauth/oauth-v2';

import { UserOnboardingModal } from './UserOnboarding';

const UserMenu: React.FunctionComponent<{ event?: EventType } & ButtonProps> = ({
    event,
    ...props
}) => {
    const { userId, userIsLoading, newUserData, userClaims, logoutUser } = useFirebaseApp();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const name = newUserData?.firstName
        ? newUserData.lastName
            ? newUserData.firstName + ' ' + newUserData.lastName
            : newUserData.firstName
        : newUserData?.displayName
          ? newUserData?.displayName
          : '';

    // Special handling for "Sonder-Academy on Health Care Policy" event
    // Allow the user menu only for admins
    if (
        event?.id === '4e582d99-b084-415e-9710-f25171b0d8cc' &&
        (!userId || !userClaims || userClaims.role !== 'admin')
    ) {
        return null;
    }

    // Special handling for "Inselspital Bern - 16. Berner Ultraschall- und Perinatalkongress" event
    // @todo: remove this, once the transition to V2 OAuth is ready
    if (event?.id === 'cc113f97-68d3-4497-b78a-f950391aee93') {
        return null;
    }

    return (
        <HStack spacing="2">
            {userId ? (
                <Box position="relative" zIndex="999">
                    <Menu>
                        <MenuButton
                            variant="outline"
                            border="none"
                            as={Button}
                            rightIcon={<FaChevronDown />}
                        >
                            <HStack as="span">
                                <Avatar size="sm" name={name || 'Unbekannt'} />
                                <Box
                                    display={{ base: 'none', lg: 'block' }}
                                    isTruncated
                                    maxWidth="140px"
                                >
                                    {name}
                                </Box>
                            </HStack>
                        </MenuButton>
                        <Portal>
                            <MenuList>
                                <MenuItem
                                    onClick={async () => {
                                        await logoutUser();
                                    }}
                                >
                                    <FormattedMessage
                                        id="nav.main.user.logout"
                                        defaultMessage="Ausloggen"
                                    />
                                </MenuItem>
                            </MenuList>
                        </Portal>
                    </Menu>
                </Box>
            ) : (
                <>
                    <Button
                        leftIcon={<FaUser />}
                        size="md"
                        minWidth="160px"
                        variant="solid"
                        colorScheme="brand"
                        onClick={() => {
                            if (!redirectToLogin(event)) {
                                onOpen();
                            }
                        }}
                        {...props}
                    >
                        {userIsLoading ? (
                            <Spinner color="highlight" />
                        ) : (
                            <FormattedMessage id="nav.main.user.login" defaultMessage="Anmelden" />
                        )}
                    </Button>
                    <UserOnboardingModal
                        event={event}
                        isOpen={isOpen}
                        onClose={onClose}
                        initialForm="login"
                    />
                </>
            )}
        </HStack>
    );
};

export default UserMenu;
